import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import SEO from "@/components/app/SEO";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Renders markdown safely; internal absolute links (resiliationexpresse.fr) and
// relative links become React Router <Link> for SPA nav + SEO. External links open new tab.
const MarkdownLink = ({ href, children }) => {
  const url = href || "";
  const internal = url.match(/^https?:\/\/resiliationexpresse\.fr(\/.*)?$/);
  if (internal) {
    return <Link to={internal[1] || "/"} className="text-primary hover:underline" data-testid="md-internal-link">{children}</Link>;
  }
  if (url.startsWith("/")) {
    return <Link to={url} className="text-primary hover:underline" data-testid="md-internal-link">{children}</Link>;
  }
  return <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>;
};
const MD_COMPONENTS = { a: MarkdownLink };

export function GuidesPage() {
  const [guides, setGuides] = useState([]);
  useEffect(() => { api.get("/guides").then((r) => setGuides(r.data)); }, []);
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
      <SEO title="Guides de résiliation" description="Guides pratiques pour résilier vos contrats et abonnements en toute sérénité." canonical="/guides" />
      <h1 className="text-4xl font-bold tracking-tight mb-2">Guides pratiques</h1>
      <p className="text-muted-foreground mb-10">Nos conseils pour résilier sereinement chaque type de contrat.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {guides.map((g) => (
          <Link key={g.slug} to={`/guides/${g.slug}`} className="card-elevated p-6" data-testid={`guide-${g.slug}`}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{g.category_slug}</div>
            <h2 className="text-lg font-semibold mb-2">{g.title}</h2>
            <p className="text-sm text-muted-foreground">{g.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function GuideDetailPage() {
  const slug = window.location.pathname.split("/").pop();
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/guides/${slug}`).then((r) => setG(r.data)).catch(() => setG(false)); }, [slug]);
  useEffect(() => {
    if (!g || g === false) return;
    const url = `https://resiliationexpresse.fr/guides/${slug}`;
    const jsonld = [
      { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://resiliationexpresse.fr/" },
        { "@type": "ListItem", "position": 2, "name": "Guides", "item": "https://resiliationexpresse.fr/guides" },
        { "@type": "ListItem", "position": 3, "name": g.title, "item": url },
      ]},
      { "@context": "https://schema.org", "@type": "Article", "headline": g.title, "description": g.excerpt || "",
        "mainEntityOfPage": url, "inLanguage": "fr-FR",
        "publisher": { "@type": "Organization", "name": "Résiliation Express", "url": "https://resiliationexpresse.fr" } },
    ];
    let el = document.getElementById("guide-jsonld");
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "guide-jsonld"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(jsonld);
    return () => { const e = document.getElementById("guide-jsonld"); if (e) e.remove(); };
  }, [g, slug]);
  if (g === false) return <div className="p-8">Guide introuvable</div>;
  if (!g) return <div className="p-8">Chargement...</div>;
  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <SEO title={g.title} description={g.excerpt} canonical={`/guides/${slug}`} />
      <nav aria-label="Fil d'Ariane" className="text-xs text-muted-foreground mb-3">
        <Link to="/" className="hover:underline">Accueil</Link> · <Link to="/guides" className="hover:underline">Guides</Link> · <span>{g.title}</span>
      </nav>
      <h1 className="text-4xl font-bold tracking-tight mt-2 mb-4">{g.title}</h1>
      <p className="text-muted-foreground mb-8">{g.excerpt}</p>
      <div className="prose dark:prose-invert max-w-none leading-relaxed" data-testid="guide-article-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{g.content}</ReactMarkdown>
      </div>
      <div className="mt-10 pt-6 border-t border-border text-sm">
        <div className="flex flex-wrap gap-3">
          <Link to="/guides" className="text-primary hover:underline">← Tous les guides</Link>
          <Link to="/modeles-lettres" className="text-primary hover:underline">Modèles de lettres</Link>
          <Link to="/annuaire" className="text-primary hover:underline">Rechercher un organisme</Link>
        </div>
      </div>
    </div>
  );
}

// ---------- Blog (réutilise l'apparence des Guides) ----------
export function BlogPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
      <SEO title="Blog — Conseils et actualités résiliation" description="Articles pratiques sur la résiliation de contrats en France : erreurs à éviter, preuves, recours, procédures." canonical="/blog" />
      <h1 className="text-4xl font-bold tracking-tight mb-2">Blog</h1>
      <p className="text-muted-foreground mb-10">Conseils, analyses et retours d'expérience sur la résiliation en France.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((a) => (
          <Link key={a.slug} to={`/blog/${a.slug}`} className="card-elevated p-6" data-testid={`blog-${a.slug}`}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{(a.tags || [])[0] || "article"}</div>
            <h2 className="text-lg font-semibold mb-2">{a.title}</h2>
            <p className="text-sm text-muted-foreground">{a.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BlogDetailPage() {
  const slug = window.location.pathname.split("/").pop();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setA(r.data)).catch(() => setA(false)); }, [slug]);
  useEffect(() => {
    if (!a || a === false) return;
    const url = `https://resiliationexpresse.fr/blog/${slug}`;
    const jsonld = [
      { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://resiliationexpresse.fr/" },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://resiliationexpresse.fr/blog" },
        { "@type": "ListItem", "position": 3, "name": a.title, "item": url },
      ]},
      { "@context": "https://schema.org", "@type": "Article", "headline": a.title, "description": a.excerpt || "",
        "mainEntityOfPage": url, "inLanguage": "fr-FR",
        "publisher": { "@type": "Organization", "name": "Résiliation Express", "url": "https://resiliationexpresse.fr" } },
    ];
    let el = document.getElementById("blog-jsonld");
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "blog-jsonld"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(jsonld);
    return () => { const e = document.getElementById("blog-jsonld"); if (e) e.remove(); };
  }, [a, slug]);
  if (a === false) return <div className="p-8">Article introuvable</div>;
  if (!a) return <div className="p-8">Chargement...</div>;
  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <SEO title={a.title} description={a.excerpt} canonical={`/blog/${slug}`} />
      <nav aria-label="Fil d'Ariane" className="text-xs text-muted-foreground mb-3">
        <Link to="/" className="hover:underline">Accueil</Link> · <Link to="/blog" className="hover:underline">Blog</Link> · <span>{a.title}</span>
      </nav>
      <h1 className="text-4xl font-bold tracking-tight mt-2 mb-4">{a.title}</h1>
      <p className="text-muted-foreground mb-8">{a.excerpt}</p>
      <div className="prose dark:prose-invert max-w-none leading-relaxed" data-testid="blog-article-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{a.content}</ReactMarkdown>
      </div>
      <div className="mt-10 pt-6 border-t border-border text-sm">
        <div className="flex flex-wrap gap-3">
          <Link to="/blog" className="text-primary hover:underline">← Tous les articles</Link>
          <Link to="/guides" className="text-primary hover:underline">Guides pratiques</Link>
          <Link to="/annuaire" className="text-primary hover:underline">Rechercher un organisme</Link>
        </div>
      </div>
    </div>
  );
}

// ---------- FAQ (rewrite) ----------
const FAQ_CATEGORIES = [
  {
    id: "fonctionnement",
    title: "Fonctionnement de Résiliation Express",
    items: [
      { q: "Qu'est-ce que Résiliation Express ?", a: "Résiliation Express est un service en ligne d'accompagnement administratif. Nous vous aidons à identifier le bon interlocuteur, générons un courrier de résiliation personnalisé conforme au droit français, et pouvons l'envoyer pour vous en recommandé avec accusé de réception. Nous ne rendons pas de consultation juridique personnalisée." },
      { q: "Résiliation Express est-elle une compagnie d'assurance ?", a: "Non. Résiliation Express n'est ni une compagnie d'assurance, ni une mutuelle, ni un courtier en assurance, ni un organisme public. Nous sommes un service indépendant d'accompagnement administratif." },
      { q: "Résiliation Express est-elle affiliée aux organismes référencés ?", a: "Non, sauf indication expresse contraire sur une fiche organisme. Les noms, marques et logos affichés dans l'annuaire restent la propriété exclusive de leurs titulaires. Notre annuaire est établi à partir de sources publiques et vérifié régulièrement." },
      { q: "Quel est le rôle exact de Résiliation Express ?", a: "Nous fournissons trois choses : (1) un annuaire à jour des coordonnées de résiliation, (2) un outil de génération de courriers personnalisés, et (3) une prestation optionnelle d'envoi en recommandé avec suivi et archivage des preuves. Nous ne signons pas de courrier à votre place et nous n'agissons pas au nom d'un organisme." },
    ],
  },
  {
    id: "dossier",
    title: "Création et suivi d'un dossier",
    items: [
      { q: "Quels contrats peuvent être concernés ?", a: "Tous les contrats et abonnements dont la résiliation s'effectue par courrier (recommandé ou simple) : assurances auto/habitation/santé, mutuelles, télécoms (mobile, box, fibre), énergie, banque, presse, streaming, salles de sport, transports, presse, sociétés d'autoroute, etc. Certains contrats disposent de leur propre procédure en ligne — nous vous l'indiquons quand c'est le cas." },
      { q: "Comment démarrer une demande ?", a: "Recherchez votre organisme dans l'annuaire, cliquez sur « Résilier », remplissez le formulaire guidé (5 à 8 minutes), payez la prestation via PayPal, et nous prenons le relais pour l'envoi si vous avez choisi la formule « Envoi assisté » ou « Accompagnement complet »." },
      { q: "Quels documents faut-il fournir ?", a: "Le minimum : votre identité, votre adresse, votre numéro de contrat ou de client, et selon le motif choisi, un justificatif (déménagement, changement de situation, résiliation infra-annuelle après un an d'engagement — loi Chatel, loi Hamon). Nous ne conservons vos justificatifs que le temps strictement nécessaire." },
      { q: "Comment suivre un dossier ?", a: "Depuis votre espace client, vous voyez en temps réel l'état du dossier, le PDF du courrier généré, le numéro de suivi postal lorsqu'il est attribué, et vous pouvez télécharger toutes les preuves (courrier, accusé de réception si applicable, facture)." },
      { q: "Un résultat de résiliation est-il garanti ?", a: "Nous garantissons la préparation du courrier conforme et son envoi effectif si vous avez choisi une formule avec envoi. Nous ne pouvons pas garantir l'acceptation de la résiliation par l'organisme tiers, qui reste seul décisionnaire. En cas de refus abusif, nous vous orientons vers les voies de recours possibles (médiateur, associations de consommateurs)." },
      { q: "Quel est le délai moyen de traitement ?", a: "Génération du courrier : immédiate. Dépôt au recommandé : sous 1 à 2 jours ouvrés après paiement. Distribution : selon le prestataire postal (72h en moyenne). Traitement par l'organisme : variable, généralement 15 à 30 jours." },
    ],
  },
  {
    id: "courrier",
    title: "Courriers et envois",
    items: [
      { q: "Comment fonctionne l'envoi postal ?", a: "Nous confions le courrier à un prestataire postal français partenaire qui procède à l'envoi en recommandé avec accusé de réception. Vous recevez un numéro de suivi et vous pouvez télécharger la preuve de dépôt et, lorsque l'accusé revient, la preuve de réception." },
      { q: "Comment obtenir une preuve d'envoi ?", a: "Toutes les preuves sont automatiquement archivées dans votre espace client, dans la rubrique « Preuves ». Vous pouvez les télécharger au format PDF/ZIP à tout moment pendant 10 ans." },
    ],
  },
  {
    id: "paiement",
    title: "Paiement et facturation",
    items: [
      { q: "Comment sont calculés les frais ?", a: "Notre grille tarifaire est fixe et transparente. Offre sur mesure : 50 % de la cotisation annuelle du contrat à résilier (devis personnalisé). Envoi assisté (courrier généré + recommandé + suivi + preuves) : 9,90 € TTC. Accompagnement complet (vérification humaine, priorité, assistance dédiée) : 14,90 € TTC. Aucun frais caché." },
      { q: "Quand le paiement est-il demandé ?", a: "Uniquement après validation par vos soins du contenu du courrier généré, avant l'envoi effectif. Aucun coût n'est prélevé sans votre accord explicite." },
      { q: "Quels sont les moyens de paiement disponibles ?", a: "Le paiement est traité exclusivement par PayPal Business (compte PayPal, carte bancaire via PayPal, Paiement en 4X sous réserve d'éligibilité). Aucun autre moyen de paiement n'est proposé." },
      { q: "Où trouver mes factures ?", a: "Dans votre espace client, rubrique « Factures ». Chaque paiement complété donne lieu à une facture téléchargeable au format PDF, conservée 10 ans conformément à la législation comptable." },
    ],
  },
  {
    id: "delais",
    title: "Délais de traitement",
    items: [
      { q: "Combien de temps entre le paiement et l'envoi ?", a: "L'envoi est déclenché sous 1 à 2 jours ouvrés après paiement. Vous êtes notifié dès qu'il est confié au prestataire postal." },
      { q: "Que se passe-t-il en cas de retard ?", a: "En cas de retard imputable à nos services, vous pouvez demander un remboursement intégral via PayPal. Les retards imputables au prestataire postal ou à l'organisme destinataire ne sont pas de notre ressort." },
    ],
  },
  {
    id: "retractation",
    title: "Rétractation et annulation",
    items: [
      { q: "Comment exercer mon droit de rétractation ?", a: "Vous disposez d'un droit de rétractation de 14 jours à compter du paiement, sauf si vous avez expressément demandé le commencement immédiat de la prestation (envoi du courrier). Dans ce cas, la prestation étant pleinement exécutée avec votre accord, le droit de rétractation ne peut plus être exercé pour la partie déjà réalisée." },
      { q: "Comment demander un remboursement ?", a: "Envoyez une demande depuis la page /reclamation ou par email à l'adresse indiquée dans les mentions légales, en précisant votre numéro de dossier. Nous répondons sous 48h ouvrées. Les remboursements sont effectués par le même moyen que le paiement (PayPal)." },
    ],
  },
  {
    id: "donnees",
    title: "Données personnelles et confidentialité",
    items: [
      { q: "Comment modifier ou supprimer mes données ?", a: "Depuis votre espace client (rubrique « Profil ») ou en écrivant à notre référent à la protection des données. Nous répondons sous 30 jours au maximum. Certaines données doivent être conservées pour des obligations légales (factures : 10 ans, preuves d'envoi : durée légale)." },
      { q: "Mes données sont-elles envoyées à Google ou d'autres tiers ?", a: "Non. Nous n'envoyons à Google Analytics ou à nos outils de mesure d'audience aucune donnée personnelle : ni nom, ni email, ni téléphone, ni adresse, ni contenu de contrat. Seules des données pseudonymisées de navigation sont transmises, uniquement si vous acceptez les cookies analytiques." },
    ],
  },
  {
    id: "compte",
    title: "Compte client",
    items: [
      { q: "Comment créer un compte ?", a: "Directement depuis /inscription avec votre email et un mot de passe, ou en un clic via Google Sign-In." },
      { q: "Que faire si j'ai oublié mon mot de passe ?", a: "Utilisez la fonction « Mot de passe oublié » sur la page de connexion. Un email de réinitialisation vous sera envoyé. Si vous vous êtes inscrit via Google, connectez-vous à nouveau avec Google." },
    ],
  },
  {
    id: "courtiers",
    title: "Courtiers et partenaires",
    items: [
      { q: "Comment devenir partenaire courtier ?", a: "Contactez-nous via le formulaire courtiers (/courtiers) en précisant votre cabinet, votre SIREN et votre volume de dossiers mensuel. Nous vous ouvrons un espace professionnel avec tarifs dégressifs et outils de traitement en masse." },
    ],
  },
  {
    id: "reclamations",
    title: "Réclamations",
    items: [
      { q: "Comment contacter le service client ?", a: "Via la page /contact en choisissant la catégorie adaptée, ou depuis votre espace client. Nos délais moyens : 24 à 48h ouvrées." },
      { q: "Comment déposer une réclamation ?", a: "Utilisez la page /reclamation en indiquant votre numéro de dossier, l'objet et une description détaillée. Vous recevrez un accusé de réception avec un identifiant unique. Si notre réponse ne vous satisfait pas dans un délai raisonnable, vous pouvez saisir gratuitement, conformément aux articles L. 612-1 et suivants du Code de la consommation, le médiateur de la consommation dont nous relevons : Société Médiation Professionnelle — saisine en ligne sur https://www.mediateur-consommation-smp.fr/ ou par courrier à Société de la Médiation Professionnelle, Alteritae, 5 rue Salvaing, 12000 Rodez. Pour les questions de données personnelles, la CNIL peut également être saisie." },
    ],
  },
];

export function FAQPage() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_CATEGORIES;
    return FAQ_CATEGORIES
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [query]);

  // JSON-LD FAQPage (only actually displayed questions)
  useEffect(() => {
    const questions = filtered.flatMap((c) => c.items).slice(0, 25);
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": questions.map((it) => ({
        "@type": "Question",
        "name": it.q,
        "acceptedAnswer": { "@type": "Answer", "text": it.a },
      })),
    };
    let el = document.getElementById("faq-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "faq-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(jsonld);
    return () => { const e = document.getElementById("faq-jsonld"); if (e) e.remove(); };
  }, [filtered]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <SEO
        title="Questions fréquentes (FAQ)"
        description="Réponses aux questions les plus fréquentes sur Résiliation Express : fonctionnement, paiement, envoi postal, données personnelles, réclamations."
        canonical="/faq"
      />
      <h1 className="text-4xl font-bold tracking-tight mb-2">Questions fréquentes</h1>
      <p className="text-muted-foreground mb-6">Trouvez une réponse claire. Si votre question n'apparaît pas, contactez-nous.</p>

      <div className="relative mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <label htmlFor="faq-search" className="sr-only">Rechercher dans la FAQ</label>
        <Input id="faq-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher une question…" className="pl-10" data-testid="faq-search-input" />
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucune question ne correspond à votre recherche. <Link to="/contact" className="text-primary">Contactez-nous</Link>.</p>
      ) : (
        <div className="space-y-8">
          {filtered.map((cat) => (
            <section key={cat.id} data-testid={`faq-cat-${cat.id}`}>
              <h2 className="text-lg font-semibold mb-3">{cat.title}</h2>
              <Accordion type="single" collapsible className="w-full">
                {cat.items.map((it, idx) => {
                  const value = `${cat.id}-${idx}`;
                  return (
                    <AccordionItem key={value} value={value} data-testid={`faq-item-${cat.id}-${idx}`}>
                      <AccordionTrigger className="text-left">{it.q}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">{it.a}</AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </section>
          ))}
        </div>
      )}

      <div className="mt-12 pt-8 border-t border-border text-sm text-muted-foreground">
        Vous n'avez pas trouvé votre réponse ? <Link to="/contact" className="text-primary font-medium">Contactez-nous</Link> ou <Link to="/reclamation" className="text-primary font-medium">déposez une réclamation</Link>.
      </div>
    </div>
  );
}
